import { Grid, Paper, Typography, Box, Button, CircularProgress } from "@material-ui/core";
import PageTitle from "components/PageTitle/PageTitle";
import { useEffect, useState } from "react";
import { dbGet, dbPost, dbPostWithFormData } from "utils/DBFetchers";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import PopupMessage from "components/PopupMessage/PopupMessage";

import useStyles from "./styles";

import PharmacySelector from "components/CustomFields/PharmacySelector";
import DistributorsField from "components/CustomFields/DistributorsField";
import DateField from "components/CustomFields/DateField";


export default function PharmacyBalancesPage() {
  const classes = useStyles();
  const [balance, setBalance] = useState(0);
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();
  const [selectedRuts, setSelectedRuts] = useState([]);
  const [selectedDistributorId, setSelectedDistributorId] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [pharmacySelectorKey, setPharmacySelectorKey] = useState(0);
  const [allPharmacyRuts, setAllPharmacyRuts] = useState([]);


  const onChangeSelectedRut = (ruts) => {
    setSelectedRuts(ruts);
  };

  // Callback para recibir todas las opciones cargadas en el selector
  const onOptionsLoaded = (options) => {
    const ruts = options.filter((o) => !o.isAll && o.rut).map((o) => o.rut);
    setAllPharmacyRuts(ruts);
  };

  const onChangeDistributor = (e) => {
    setSelectedDistributorId(e.target.value);
    setSelectedRuts([]);
    setDateFrom(null);
    setDateTo(null);
    setBalance(0);
    setPharmacySelectorKey((prev) => prev + 1);
  };

  useEffect(() => {
    // No hacemos nada hasta que exista una fecha final válida
    if (!dateTo) return;

    // Si cambia la fecha final:
    // 1) vaciamos la selección
    // 2) reseteamos el balance
    // El PharmacySelector se actualizará automáticamente por el cambio de prop date_to
    setSelectedRuts([]);
    setBalance(0);
  }, [dateTo]);

  useEffect(() => {
    // Si cambia la fecha inicial:
    // 1) limpiamos fecha final
    // 2) limpiamos selección de farmacias
    // 3) reseteamos balance
    // 4) forzamos refresh del PharmacySelector
    if (!dateFrom) return;

    setDateTo(null);
    setSelectedRuts([]);
    setBalance(0);
    setPharmacySelectorKey((prev) => prev + 1);
  }, [dateFrom]);

  const loadPharmacyBalances = (ruts) => {
    // Si se seleccionó "Todas" (ruts contiene ""), usar todos los RUTs cargados
    const isAllSelected = (ruts || []).includes("");
    const rutsToQuery = isAllSelected ? allPharmacyRuts : (ruts || []).filter(r => r !== "");

    if (rutsToQuery.length === 0) {
      setBalance(0);
      return;
    }
    // Usar POST para enviar RUTs en el body (evita límite de longitud de URL)
    dbPost(`wallet/internal/balances/pharmacies/batch`, { pharmacy_ruts: rutsToQuery })
      .then((data) => {
        setBalance(data.total_balance);
      })
      .catch((error) => {
        setBalance(0);
        snackbarOpen("error", "Error de comunicación con el servidor");
      });
  }

  useEffect(() => {
    loadPharmacyBalances(selectedRuts);
  }, [selectedRuts, allPharmacyRuts]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const formData = new FormData();
      formData.append("pharmacy_ruts", JSON.stringify(selectedRuts));
      if (dateFrom) formData.append("from_date", dateFrom.toISOString().split("T")[0]);
      if (dateTo) formData.append("to_date", dateTo.toISOString().split("T")[0]);
      if (selectedDistributorId) {
        formData.append("distributor_id", selectedDistributorId);
      }

      const postData = await dbPostWithFormData(
        "cli/export_distributor_all_balances",
        formData
      );
      if (!postData?.output_files) {
        throw new Error("Respuesta inválida del servidor");
      }

      const entries = Object.entries(postData.output_files || {});
      const [filename, url] = entries[0] || [];

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      snackbarOpen("success", "Archivo generado con éxito");
    } catch (e) {
      snackbarOpen("error", "Error al generar el archivo. Intente nuevamente.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Grid>
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />
      <PageTitle title="Billetera virtual - Saldo de Farmacias" />
      <Paper elevation={3} className={classes.paperContainer} style={{ padding: 24, borderRadius: 16 }}>
        <Grid container spacing={4}>
          {/* Columna Izquierda: Filtros */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={3} direction="column">
              {/* Fila 1: Distribuidor */}
              <Grid item>
                <Typography style={{ fontWeight: 700, marginBottom: 8, color: "#555" }}>
                  Distribuidor
                </Typography>
                <DistributorsField
                  fieldClassName={classes.generalSelectionField}
                  value={selectedDistributorId}
                  onChange={onChangeDistributor}
                  only_with_wallet={true}
                  all={true}
                />
              </Grid>

              {/* Fila 2: Fechas */}
              <Grid item>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography style={{ fontWeight: 700, marginBottom: 8, color: "#555" }}>
                      Fecha inicial
                    </Typography>
                    <DateField
                      value={dateFrom}
                      onChange={setDateFrom}
                      disabled={selectedDistributorId === null}
                      maxDate={new Date()}
                      format="dd/MM/yyyy"
                      fieldClassName={classes.generalSelectionField}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography style={{ fontWeight: 700, marginBottom: 8, color: "#555" }}>
                      Fecha final
                    </Typography>
                    <DateField
                      value={dateTo}
                      onChange={setDateTo}
                      disabled={!dateFrom}
                      minDate={dateFrom}
                      maxDate={new Date()}
                      format="dd/MM/yyyy"
                      fieldClassName={classes.generalSelectionField}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Fila 3: Farmacias con billetera */}
              <Grid item>
                <Typography style={{ fontWeight: 700, marginBottom: 8, color: "#555" }}>
                  {`Farmacias con billetera${selectedDistributorId === -1 ? "" : " asociadas al distribuidor en fecha final"}`}
                </Typography>
                <PharmacySelector
                  key={pharmacySelectorKey}
                  fieldClassName={classes.pharmacySelectionField}
                  value={selectedRuts}
                  onChange={onChangeSelectedRut}
                  onOptionsLoaded={onOptionsLoaded}
                  program={1}
                  only_with_wallet={true}
                  distributor_id={selectedDistributorId === -1 ? null : selectedDistributorId}
                  disabled={!dateTo}
                  multiple={true}
                  include_all={true}
                  isWallet={true}
                  date_to={dateTo}
                />

              </Grid>
            </Grid>
          </Grid>

          {/* Columna Derecha: Saldo y Botón */}
          <Grid item xs={12} md={4}>
            <Box display="flex" flexDirection="column" height="100%" justifyContent="space-between" alignItems="center">
              {/* Saldo de farmacias */}
              <Box textAlign="center" width="100%" mt={2}>
                <Typography style={{ fontWeight: 700, marginBottom: 12, color: "#555" }}>
                  Saldo de farmacias
                </Typography>
                <Paper
                  elevation={2}
                  style={{
                    borderRadius: 12,
                    background: "#f9f9f9",
                    padding: "16px",
                    border: "1px solid #eee",
                    minHeight: 60,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <Typography variant="h5" style={{ fontWeight: 700, color: "#2e7d32" }}>
                    {(selectedRuts.length > 0 && selectedRuts.some(r => r !== "")) ||
                      (selectedRuts.includes("") && allPharmacyRuts.length > 0) ? (
                      `$${new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      }).format(balance)}`
                    ) : (
                      "-"
                    )}
                  </Typography>
                </Paper>
              </Box>

              {/* Botón Descargar composición */}
              <Box width="100%" mb={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleDownload}
                  disabled={downloading || selectedRuts.length === 0}
                  className={classes.downloadButtonLarge}
                  startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : null}
                  style={{
                    height: 120,
                    borderRadius: 16,
                    fontSize: "1.2rem",
                    fontWeight: 700,
                    textTransform: "none",
                    backgroundColor: (downloading || selectedRuts.length === 0) ? "#f0f0f0" : "#e8f5e9",
                    color: (downloading || selectedRuts.length === 0) ? "#aaa" : "#2e7d32",
                    border: (downloading || selectedRuts.length === 0) ? "2px solid #ddd" : "2px solid #2e7d32",
                    boxShadow: (downloading || selectedRuts.length === 0) ? "none" : "0 4px 12px rgba(46, 125, 50, 0.2)"
                  }}
                >
                  {downloading ? "Generando..." : "Descargar composición"}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
}
